import { useStore } from "@tanstack/react-form";
import dayjs from "dayjs";
import { ExternalLinkIcon, PlusIcon, XIcon } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import React from "react";
import { Focusable } from "react-aria-components";

import {
  TestMenu,
  TestMenuOrgFilterSwitch,
  TestMenuProvider,
  TestMenuSearch,
  TestMenuTestTypeSelect,
} from "@/components/TestMenu";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { linkStyles } from "@/components/ui/link";
import { Modal } from "@/components/ui/modal";
import { Separator } from "@/components/ui/separator";
import { Text } from "@/components/ui/text";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { useCacheValues } from "@/lib/cache";
import { useAccessionForm } from "./AccessionFormProvider";
import { FormSection } from "./layout";
import type { Instruction } from "./schema";

export function TestsSection() {
  const { form } = useAccessionForm();
  const meta = useStore(form.store, (s) => s.fieldMeta.instructions);
  return (
    <FormSection
      title="Test orders"
      description="Select test panel(s) or a la carte tests."
      actions={<AddTestsDialog />}
      error={meta?.errors?.join(", ")}
    >
      <TestList />
    </FormSection>
  );
}

function TestList() {
  const { form, setPreferredTubes } = useAccessionForm();
  const instructions = useStore(form.store, (s) => s.values.instructions);

  const panels = instructions?.filter((i) => i.type === "panel") ?? [];
  const tests = instructions?.filter((i) => i.type === "test") ?? [];

  const sortedArr: Instruction[] = [];

  sortedArr.push(...panels);
  sortedArr.push(...tests);

  return (
    <AnimatePresence>
      {sortedArr.map((instruction) => (
        <motion.div
          key={
            instruction.type === "panel"
              ? `panel-${instruction.panelId}`
              : `test-${instruction.testId}`
          }
          className="overflow-hidden bg-background last:rounded-b-md"
          initial={{ height: 0 }}
          animate={{ height: "auto" }}
          exit={{ height: 0 }}
        >
          <TestListItem
            instruction={instruction}
            onRemove={() => {
              const filtered = sortedArr.filter((i) => i !== instruction);
              form.setFieldValue("instructions", filtered);
              setPreferredTubes(filtered);
            }}
          />
        </motion.div>
      ))}
    </AnimatePresence>
  );
}

function TestListItem({
  instruction,
  onRemove,
}: {
  instruction: Instruction;
  onRemove: () => void;
}) {
  const { accessions } = useAccessionForm();
  const { testById, panelById } = useCacheValues();

  const getExistingAccessions = React.useCallback(
    (instruction: Instruction) => {
      if (instruction.type === "test") {
        return accessions.filter((a) =>
          a.instructions.some((to) => to.testId === instruction.testId),
        );
      }
      return accessions.filter((a) =>
        a.instructions.some((to) => to.panelId === instruction.panelId),
      );
    },
    [accessions],
  );

  const getInstructionName = React.useCallback(
    (instruction: Instruction) => {
      if (instruction.type === "test") {
        return testById(instruction.testId)?.name;
      }
      return panelById(instruction.panelId)?.name;
    },
    [testById, panelById],
  );

  const existing = getExistingAccessions(instruction);

  return (
    <div className="flex items-center justify-between gap-2 px-4 py-2">
      <div className="flex items-center gap-2 shrink overflow-hidden">
        {instruction.type === "panel" && <Badge>Panel</Badge>}
        <Text truncate>{getInstructionName(instruction)}</Text>
      </div>
      <div className="flex items-center gap-2">
        {existing.length > 0 && (
          <TooltipTrigger delay={200} closeDelay={100}>
            <Focusable>
              <Badge>{existing.length}</Badge>
            </Focusable>
            <Tooltip className="bg-background border border-border text-foreground min-w-60">
              <Text bold>
                Ordered {existing.length} time{existing.length !== 1 ? "s" : ""}
              </Text>
              <Separator className="my-2" />
              <ul>
                {existing.map((a) => (
                  <li key={a.id} className="flex items-center justify-between">
                    <a
                      href={`/accessions/${a.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={linkStyles({
                        className: "flex items-center gap-1",
                      })}
                    >
                      {a.id}
                      <ExternalLinkIcon className=" size-3" />
                    </a>
                    <Text size="1" muted>
                      {dayjs(a.date).fromNow()}
                    </Text>
                  </li>
                ))}
              </ul>
            </Tooltip>
          </TooltipTrigger>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="size-6"
          type="button"
          onPress={onRemove}
        >
          <XIcon className="size-3" />
        </Button>
      </div>
    </div>
  );
}

function AddTestsDialog() {
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectedPanels, setSelectedPanels] = React.useState<Set<number>>(
    new Set(),
  );
  const [selectedTests, setSelectedTests] = React.useState<Set<number>>(
    new Set(),
  );
  const inferredTestType = useInferredTestType();

  const { form, setPreferredTubes } = useAccessionForm();

  const orgId = useStore(form.store, (s) => s.values.orgId);
  const instructions = useStore(form.store, (s) => s.values.instructions);

  React.useEffect(() => {
    setSelectedPanels(
      new Set(
        instructions?.filter((i) => i.type === "panel").map((i) => i.panelId),
      ),
    );
    setSelectedTests(
      new Set(
        instructions?.filter((i) => i.type === "test").map((i) => i.testId),
      ),
    );
  }, [instructions]);

  const onSave = React.useCallback(() => {
    const instructions = [
      ...Array.from(selectedPanels).map((id) => ({
        type: "panel" as const,
        panelId: id,
      })),
      ...Array.from(selectedTests).map((id) => ({
        type: "test" as const,
        testId: id,
      })),
    ];

    form.setFieldValue("instructions", instructions);
    setPreferredTubes(instructions);
    setIsOpen(false);
  }, [form, selectedPanels, selectedTests, setPreferredTubes]);

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
      <Button variant="outline" isDisabled={!orgId}>
        <PlusIcon />
        Add tests
      </Button>
      <TestMenuProvider
        orgId={orgId}
        testType={inferredTestType}
        selectedPanels={selectedPanels}
        selectedTests={selectedTests}
        onSelectedPanelsChange={setSelectedPanels}
        onSelectedTestsChange={setSelectedTests}
      >
        <Modal size="2xl">
          <Dialog>
            <DialogHeader className="border-b">
              <DialogTitle className="shrink-0">Add tests</DialogTitle>
              <div className="pt-2 flex items-center gap-4">
                <TestMenuSearch className="flex-1" />
                <TestMenuTestTypeSelect />
              </div>
            </DialogHeader>

            <DialogBody className="p-0 sm:p-0">
              <div className="flex-1 flex flex-col min-h-[400px] max-h-[400px]">
                <TestMenu />
              </div>
            </DialogBody>
            <DialogFooter className="border-t sm:justify-between">
              <div className="flex items-center gap-2 flex-row-reverse">
                <Button onPress={onSave}>Save</Button>
                <DialogCloseButton>Cancel</DialogCloseButton>
              </div>
              <TestMenuOrgFilterSwitch />
            </DialogFooter>
          </Dialog>
        </Modal>
      </TestMenuProvider>
    </DialogTrigger>
  );
}

function useInferredTestType() {
  const { form } = useAccessionForm();
  const instructions = useStore(form.store, (s) => s.values.instructions);
  const { panelById, testById } = useCacheValues();

  const testTypes = React.useMemo(() => {
    const types = new Set<string>();

    if (!instructions) {
      return undefined;
    }

    for (const instruction of instructions) {
      if (instruction.type === "test") {
        const test = testById(instruction.testId);
        if (test) {
          types.add(test.type);
        }
      } else if (instruction.type === "panel") {
        const panel = panelById(instruction.panelId);
        if (panel) {
          for (const test of panel.tests) {
            types.add(test.type);
          }
          for (const subPanel of panel.panels) {
            for (const test of subPanel.tests) {
              types.add(test.type);
            }
          }
        }
      }
    }

    return types;
  }, [instructions, panelById, testById]);

  return testTypes?.values().next().value;
}
